<template>
  <div class="working-hours">
    <ul
      v-if="workingHours"
      class="working-hours-list"
    >
      <li
        v-for="(workingHour, index) in workingHours"
        v-if="workingHour"
        :key="index"
        class="row"
      >
        <div class="small-4">
          {{ workingHour.workingDesc }}
        </div>
        <div class="working-day-from small-4">
          {{ joinDayString(workingHour) }}
        </div>
        <div class="small-3">
          {{ joinHourString(workingHour) }}
        </div>
        <button
          v-show="!disabled"
          role="delete-button"
          class="fa fa-times fa-lg"
          :class="`delete-working-hour-${index}`"
          :aria-hidden="true"
          @click="deleteWorkingHour(index)"
        />
      </li>
    </ul>
    <hr v-if="showWorkingHours && !disabled">

    <div
      v-show="!disabled"
      class="working-hours-add"
    >
      <InputField
        v-model="workingDesc"
        :value="workingDesc"
        :required="true"
        :placeholder="$t('hint.holidayHourPlaceholder')"
        class="small-8"
      />
      <div class="working-hours-date-time row">
        <DatePicker
          v-model="workingDayFrom"
          class="working-day hkl-select"
          format="DD MMM YYYY"
          :calendarButton="true"
          :placeholder="$t('dateFrom')"
          :required="true"
          :disabled="disabledDatesForStartDate(workingDayTo)"
          calendarButtonIcon="fa fa-calendar"
        />
        <div class="dash" />
        <DatePicker
          v-model="workingDayTo"
          format="DD MMM YYYY"
          class="working-day hkl-select"
          :calendarButton="true"
          :placeholder="$t('dateTo')"
          :required="true"
          :disabled="disabledDatesForEndDate(workingDayFrom)"
          calendarButtonIcon="fa fa-calendar"
        />

        <HKLSelect
          class="working-hour-from"
          name="workingHourFrom"
          :class="{ 'error-highlight': invalidWorkingHour || (isMandatory && emptyWorkingHours) }"
          :value="workingHourFrom || undefined"
          :options="workingHourFromHklOptions"
          :showSearch="true"
          placeholder=""
          @change="workingHourFrom = $event || ''"
        />
        <div class="dash" />
        <HKLSelect
          class="working-hour-to"
          name="workingHourTo"
          :class="{ 'error-highlight': invalidWorkingHour || (isMandatory && emptyWorkingHours)}"
          :disabled="disableHourTo"
          :value="workingHourTo || undefined"
          :options="workingHourToHklOptions"
          :showSearch="true"
          placeholder=""
          @change="workingHourTo = $event || ''"
        />
        <button
          id="modules-tenders-workingHours-add"
          :disabled="disableButton()"
          type="button"
          role="add-button"
          class="btn btn--darkblue btn-add add-working-hour"
          name="add-working-hour"
          @click="addWorkingHour"
        >
          {{ $t('action.add') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import lodash from 'lodash'
import InputField from '@/controls/InputField'

import { utils as formUtils } from '@/plugin/InputChange'
import { workingHourOptions, workingDayOptions, workingHourAnytime } from '@/utils/consts'
import DatePicker from '@/controls/DatePicker'
import dateUtil from '@/utils/dateUtil'

export default {
  name: 'HolidayOpeningHours',
  components: {
    HKLSelect,
    DatePicker,
    InputField,
  },
  props: {
    workingHours: {
      type: Array,
      default: () => [],
    },
    updateValueByPath: {
      type: Function,
      default: () => {
        return function () {}
      },
    },
    fieldPath: {
      type: Array,
      default: () => [],
    },
    emptyWorkingHours: {
      type: Boolean,
      default: false,
    },
    isMandatory: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      workingDesc: '',
      workingHourOptions,
      workingDayOptions,
      workingDayFrom: '',
      workingDayTo: '',
      workingHourFrom: '',
      workingHourTo: '',
      invalidWorkingHour: false,
      formUtils,
    }
  },

  computed: {
    showWorkingHours () {
      return (this.workingHours || []).length
    },
    disableHourTo () {
      return this.workingHourFrom === workingHourOptions[0]
    },
    workingHourFromHklOptions () {
      return this.mapStringOptions(this.workingHourOptions, this.customWorkingHourLabel)
    },
    workingHourToHklOptions () {
      return this.mapStringOptions(
        this.workingHourOptions.slice(1, this.workingHourOptions.length),
        this.customWorkingHourLabel,
      )
    },
  },

  methods: {
    mapStringOptions (options, labelFn) {
      return (options || []).map((value) => ({
        name: labelFn(value),
        value,
      }))
    },
    customWorkingDayLabel (option) {
      return this.$options.filters.customWorkingDayLabel(option)
    },
    customWorkingHourLabel (option) {
      return this.$options.filters.customWorkingHourLabel(option)
    },

    joinDayString (workingHour) {
      const workingDayTo = workingHour.workingDayTo && workingHour.workingDayTo !== workingHour.workingDayFrom ? ` - ${this.formattedDate(workingHour.workingDayTo)}` : ''
      return `${this.formattedDate(workingHour.workingDayFrom)}${workingDayTo}`
    },
    joinHourString (workingHour) {
      const workingHourTo = workingHour.workingHourTo && workingHour.workingHourTo !== workingHour.workingHourFrom ? ` - ${workingHour.workingHourTo}` : ''
      return `${this.customWorkingHourLabel(workingHour.workingHourFrom)}${workingHourTo}`
    },
    resetWorkingHour () {
      this.workingDesc = ''
      this.workingDayFrom = ''
      this.workingDayTo = ''
      this.workingHourFrom = ''
      this.workingHourTo = ''
      this.invalidWorkingHour = false
    },
    formattedDate (date) {
      return dateUtil.format(date, dateUtil.DD_MMM_YYYY)
    },

    validateMandatory () {
      if (!this.isMandatory) {
        return true
      }
      if (lodash.isEmpty(this.workingHours)) {
        this.invalidWorkingHour = true
        return false
      }
      return true
    },
    disabledDatesForStartDate (workingDayTo) {
      const toDate = dateUtil.getTodayStartDateObject()
      if (workingDayTo) {
        const fromDate = new Date(workingDayTo)
        fromDate.setDate(fromDate.getDate())
        return {
          from: fromDate,
          to: toDate,
        }
      }
      return {
        to: toDate,
      }
    },
    disabledDatesForEndDate (workingDayFrom) {
      let toDate
      if (workingDayFrom && (new Date(workingDayFrom) > new Date())) {
        toDate = new Date(workingDayFrom)
      } else {
        toDate = dateUtil.getTodayStartDateObject()
        toDate.setDate(toDate.getDate())
      }
      return {
        to: toDate,
      }
    },

    disableButton () {
      return !((this.workingHourFrom && (this.workingHourFrom === workingHourAnytime || this.workingHourTo)) && (this.workingDayFrom && this.workingDayTo) && this.workingDesc)
    },

    addWorkingHour () {
      if (this.workingHourFrom === workingHourAnytime) {
        this.workingHourTo = ''
      }
      const workingHour = {
        workingDesc: this.workingDesc,
        workingDayFrom: this.workingDayFrom,
        workingDayTo: this.workingDayTo,
        workingHourFrom: this.workingHourFrom,
        workingHourTo: this.workingHourTo,
      }
      const workingHours = lodash.cloneDeep(this.workingHours)
      workingHours.push(workingHour)
      this.updateValueByPath({
        path: this.fieldPath,
        newValue: workingHours,
      })
      this.resetWorkingHour()
      formUtils.setDirty()
    },

    deleteWorkingHour (index) {
      const workingHours = lodash.cloneDeep(this.workingHours)
      workingHours.splice(index, 1)
      this.updateValueByPath({
        path: this.fieldPath,
        newValue: workingHours,
      })
      formUtils.setDirty()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/components/working-hours';
.working-hours-add {
  flex-wrap: wrap;
}
.working-hours-date-time {
  width: 100%;
  flex-wrap: nowrap;
}
.working-day-from {
  margin-right: 10px;

}
.working-hour-from {
  margin-left: 12px;
}
</style>
